// Dining Staff data layer — READS team names from the active qualifying event and
// WRITES only to diningMessages/{eid}. Imports ONLY from src/lib (shared firebase
// + qualifying-event lib) — NEVER from the admin / pro-shop program. The dining
// app has no capability to write scores/tournaments/flights: it references none.
import { ref, push, set, onValue } from 'firebase/database'
import { db } from '../lib/firebase.js'
import { watchQualifyingEvents, watchQualifyingEvent } from '../lib/qualifyingEvents.js'

// Newest OPEN, non-practice event; prefer 4-Ball — same rule the app uses.
function pickActiveId(events) {
  const open = Object.entries(events || {})
    .filter(([, v]) => v?.info?.status !== 'closed' && !v?.info?.isPractice)
    .sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
  const fourBall = open.filter(([, v]) => String(v?.info?.format || '').includes('4-Ball'))
  const chosen = fourBall[0] || open[0]
  return chosen ? chosen[0] : null
}

// Watch the active event + its teams. cb({ eid, eventName, teams:[{teamId,player1,player2,teamName}] }).
export function watchActiveDiningField(cb) {
  let offEvent = null
  const offList = watchQualifyingEvents((events) => {
    const eid = pickActiveId(events)
    if (offEvent) { offEvent(); offEvent = null }
    if (!eid) { cb({ eid: null, eventName: '', teams: [] }); return }
    offEvent = watchQualifyingEvent(eid, (ev) => {
      const teams = Object.entries(ev?.teams || {}).map(([teamId, t]) => ({
        teamId,
        teamName: t?.teamName || teamId,
        player1: t?.player1 || '',
        player2: t?.player2 || '',
      }))
      cb({ eid, eventName: String(ev?.info?.name || '').split('\n')[0], teams })
    })
  })
  return () => { if (offEvent) offEvent(); offList() }
}

const lastName = (name) => String(name || '').trim().split(/\s+/).pop()?.toLowerCase() || ''
export const playersOf = (t) => [t.player1, t.player2].filter(Boolean)
export const teamLabel = (t) => playersOf(t).join(' / ') || t.teamName || t.teamId

// Deterministic last-name match — NEVER invents. Returns matching teams; each
// carries which player(s) matched so staff can drill to an individual.
export function lookupByLastName(teams, query) {
  const q = String(query || '').trim().toLowerCase().replace(/[^a-z\s]/g, '')
  if (!q) return []
  const tokens = q.split(/\s+/).filter(Boolean)
  const out = []
  for (const t of teams) {
    const matched = playersOf(t).filter((p) => tokens.includes(lastName(p)))
    if (matched.length) out.push({ ...t, matchedPlayers: matched })
  }
  return out.sort((a, b) => teamLabel(a).localeCompare(teamLabel(b)))
}

// READ-ONLY watch of the scheduled dinner reminders (set-and-forget, fired by the
// flushScheduledDiningReminders cron). Returns them sorted by fire time so the
// dining tool can show Mark the queue. Viewing never sends anything.
export function watchScheduledDiningReminders(cb) {
  return onValue(ref(db, 'scheduledDiningReminders'), (snap) => {
    const v = snap.val() || {}
    const list = Object.entries(v)
      .map(([id, r]) => ({ id, ...(r || {}) }))
      .sort((a, b) => (Number(a.fireAtMs) || 0) - (Number(b.fireAtMs) || 0))
    cb(list)
  }, () => cb([]))
}

// Write ONE dining message with N targets (teams and/or individuals) → the
// overlay + push pipeline delivers it. targets: [{kind:'team',teamId,teamName} |
// {kind:'player',teamId,player}].
export async function sendDiningMessage(eid, text, targets) {
  if (!eid || !text || !Array.isArray(targets) || targets.length === 0) throw new Error('Nothing to send.')
  const node = push(ref(db, `diningMessages/${eid}`))
  const now = Date.now()
  await set(node, {
    msgId: node.key,
    text: String(text).trim(),
    targets,
    sendAt: now,
    postedAt: now,
    source: 'dining',
  })
  return node.key
}

export { onValue }
